// Inverse Distance Weighted interpolation for per-zone moisture heatmaps.
//
// Pure client-side compute — cheap enough even at 10×10 resolution per zone
// (~100 points × ~18 zones × 54 sensors = sub-millisecond on modern laptops).
// Moving to server-side + precompute is a future optimization if sensor
// density climbs past ~200 total.

import type { PlacedSensor, Zone } from '@greeniq/shared';

const DEFAULT_GRID = 10; // 10×10 cells per zone
const POWER = 2; // IDW exponent
const MAX_INFLUENCE_METERS = 100; // beyond this, a sensor has no weight

const METERS_PER_DEGREE_LAT = 111_320;

export interface GridCell {
  lng: number;
  lat: number;
  /** null when no sensor within MAX_INFLUENCE_METERS. */
  vwc: number | null;
}

function zoneBbox(zone: Zone): [number, number, number, number] | null {
  const coords = zone.polygon.type === 'Polygon'
    ? zone.polygon.coordinates
    : zone.polygon.coordinates.flat();
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const ring of coords) {
    for (const [lng, lat] of ring as [number, number][]) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  }
  if (!Number.isFinite(minLng)) return null;
  return [minLng, minLat, maxLng, maxLat];
}

function pointInRing(point: [number, number], ring: [number, number][]): boolean {
  // Ray casting. Ring must be closed (first === last).
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    const intersect = ((yi > point[1]) !== (yj > point[1]))
      && (point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInZone(point: [number, number], zone: Zone): boolean {
  const polygons = zone.polygon.type === 'Polygon'
    ? [zone.polygon.coordinates]
    : zone.polygon.coordinates;
  for (const poly of polygons) {
    const outer = poly[0] as [number, number][];
    if (!outer) continue;
    if (!pointInRing(point, outer)) continue;
    let inHole = false;
    for (let h = 1; h < poly.length; h++) {
      if (pointInRing(point, poly[h] as [number, number][])) {
        inHole = true;
        break;
      }
    }
    if (!inHole) return true;
  }
  return false;
}

function distMeters(aLng: number, aLat: number, bLng: number, bLat: number): number {
  const mPerLon = METERS_PER_DEGREE_LAT * Math.cos((aLat * Math.PI) / 180);
  const dx = (bLng - aLng) * mPerLon;
  const dy = (bLat - aLat) * METERS_PER_DEGREE_LAT;
  return Math.hypot(dx, dy);
}

/** Compute a gridded IDW surface for one zone from sensors with known VWC. */
export function idwGridForZone(
  zone: Zone,
  sensors: PlacedSensor[],
  gridSize = DEFAULT_GRID,
): GridCell[] {
  const bbox = zoneBbox(zone);
  if (!bbox) return [];
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const sensorsWithValue = sensors.filter(
    (s) => s.zoneId === zone.id && s.latestVwc !== null,
  );
  if (sensorsWithValue.length === 0) return [];

  const cells: GridCell[] = [];
  for (let i = 0; i <= gridSize; i++) {
    for (let j = 0; j <= gridSize; j++) {
      const lng = minLng + ((maxLng - minLng) * i) / gridSize;
      const lat = minLat + ((maxLat - minLat) * j) / gridSize;
      if (!pointInZone([lng, lat], zone)) continue;

      let weightSum = 0;
      let valueSum = 0;
      let exactHit = false;
      for (const s of sensorsWithValue) {
        const d = distMeters(lng, lat, s.lng, s.lat);
        if (d < 0.5) {
          valueSum = s.latestVwc!;
          weightSum = 1;
          exactHit = true;
          break;
        }
        if (d > MAX_INFLUENCE_METERS) continue;
        const w = 1 / Math.pow(d, POWER);
        weightSum += w;
        valueSum += w * (s.latestVwc as number);
      }
      cells.push({
        lng, lat,
        vwc: weightSum > 0 ? (exactHit ? valueSum : valueSum / weightSum) : null,
      });
    }
  }
  return cells;
}

/** Map a VWC fraction to the same color scale as the sensor-dot legend. */
export function heatmapColor(vwc: number | null): string {
  if (vwc === null || !Number.isFinite(vwc)) return 'rgba(156,163,175,0.0)';
  if (vwc < 0.10) return 'rgba(220, 38, 38, 0.55)'; // red-600 critical
  if (vwc < 0.13) return 'rgba(245, 158, 11, 0.55)'; // amber-500 warn
  if (vwc > 0.35) return 'rgba(37, 99, 235, 0.45)'; // blue-600 saturated
  return 'rgba(22, 163, 74, 0.45)'; // green-600 healthy
}

/** Hex color for the per-zone heatmap fill layer (paired with the existing
 *  `fill-opacity: 0.55` on the zone layer so it reads without washing out). */
export function zoneHeatmapFill(vwc: number | null): string {
  if (vwc === null || !Number.isFinite(vwc)) return '#6b7280'; // slate — unknown
  if (vwc < 0.10) return '#ef4444'; // red-500 critical
  if (vwc < 0.13) return '#f59e0b'; // amber-500 warn
  if (vwc > 0.35) return '#3b82f6'; // blue-500 saturated
  return '#22c55e'; // green-500 healthy
}
